import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";
import { Eye, FileText, Save } from "lucide-react";
import { toast } from "../../components/ui/sonner";

export function PdfBuilderTab() {
  const KEYWORDS = [
    "{quote_number}", "{customer_name}", "{job_type}", "{category}", "{branch}",
    "{postcode}", "{estimator}", "{version}", "{date}", "{status}",
  ];

  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Live preview state
  const [previewQuotes, setPreviewQuotes] = useState([]);
  const [previewQuoteId, setPreviewQuoteId] = useState("");
  const [previewQuote, setPreviewQuote] = useState(null);

  useEffect(() => {
    api.get("/pdf-template").then((r) => setTpl(r.data));
    api.get("/quotes").then((r) => {
      const list = r.data || [];
      setPreviewQuotes(list);
      // Default to the most recent quote with actual line items, so the preview isn't blank
      const best = list.find((p) => Number(p.grand_total || 0) > 0) || list[0];
      if (best) setPreviewQuoteId(best.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!previewQuoteId) { setPreviewQuote(null); return; }
    let cancelled = false;
    api.get(`/quotes/${previewQuoteId}`).then((r) => { if (!cancelled) setPreviewQuote(r.data); });
    return () => { cancelled = true; };
  }, [previewQuoteId]);

  if (!tpl) return <div className="text-sm text-slate-500">Loading…</div>;

  const insertKeyword = (field, kw) => {
    setTpl({ ...tpl, [field]: (tpl[field] || "") + " " + kw });
  };

  // Substitute the preview's data for the keywords so admins see the final result
  const subst = (s) => {
    const q = previewQuote || {};
    return (s || "").replace(/\{(\w+)\}/g, (m, k) => {
      const map = {
        quote_number: q.quote_number || "QUO-PREVIEW",
        customer_name: q.customer_name || "Sample Customer Ltd",
        job_type: q.job_type || "Install",
        category: q.category || "Industrial Door",
        branch: q.branch || "—",
        postcode: q.postcode || "PL1 1AA",
        estimator: q.estimator || q.created_by_name || "—",
        version: String(q.version || 1),
        date: new Date().toLocaleDateString("en-GB"),
        status: q.status || "Draft",
      };
      return map[k] != null ? String(map[k]) : m;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/pdf-template", {
        title: tpl.title,
        header_text: tpl.header_text,
        footer_text: tpl.footer_text,
        show_logo: !!tpl.show_logo,
        show_attachments: !!tpl.show_attachments,
        // Sections list is kept for backwards-compat but the planner layout is fixed.
        sections: tpl.sections || [],
      });
      setTpl(data);
      setSavedAt(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pdf-builder-tab">
      <div className="text-xs text-slate-500 max-w-2xl">
        The Quote Matrix export uses your existing planner layout. Use this page to customise the
        <strong> title</strong>, <strong>header body text</strong> and <strong>footer text</strong> shown on every export.
        Use the <span className="font-mono">{"{keyword}"}</span> chips to insert live quote data.
      </div>
      <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 px-3 py-2 rounded max-w-2xl">
        <strong>Tip:</strong> The Export button produces a high-fidelity PDF of the on-screen planner (not text-selectable).
        For copy/paste-able PDFs, open a quote and press <strong>Print → Save as PDF</strong> in your browser — the browser's native printer keeps text selectable.
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* LEFT: editor */}
        <div className="space-y-6">
          <div className="qb-card p-4 space-y-3">
            <h3 className="font-display text-base font-bold uppercase tracking-wider flex items-center gap-2">
              <FileText size={16} /> Header
            </h3>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">
                Small-caps title (above the quote number)
              </label>
              <input
                value={tpl.title || ""}
                onChange={(e) => setTpl({ ...tpl, title: e.target.value })}
                className="qb-input"
                data-testid="pdf-title-input"
                placeholder="e.g. Planner Sheet · For internal scheduling"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Header body text (shown under the customer line)</label>
              <textarea
                value={tpl.header_text || ""}
                onChange={(e) => setTpl({ ...tpl, header_text: e.target.value })}
                rows={3}
                className="qb-input"
                data-testid="pdf-header-input"
                placeholder="Optional extra header text. Supports keywords."
              />
              <KeywordChips onPick={(kw) => insertKeyword("header_text", kw)} keywords={KEYWORDS} />
            </div>
          </div>

          <div className="qb-card p-4 space-y-3">
            <h3 className="font-display text-base font-bold uppercase tracking-wider flex items-center gap-2">
              <FileText size={16} /> Footer
            </h3>
            <textarea
              value={tpl.footer_text || ""}
              onChange={(e) => setTpl({ ...tpl, footer_text: e.target.value })}
              rows={4}
              className="qb-input"
              data-testid="pdf-footer-input"
              placeholder="Disclaimer, internal-use notice, generated-on line, etc."
            />
            <KeywordChips onPick={(kw) => insertKeyword("footer_text", kw)} keywords={KEYWORDS} />
            <label className="inline-flex items-center gap-2 text-xs text-slate-600 mt-2">
              <input
                type="checkbox"
                checked={!!tpl.show_attachments}
                onChange={(e) => setTpl({ ...tpl, show_attachments: e.target.checked })}
                className="w-4 h-4 accent-brand-blue"
                data-testid="pdf-attachments-toggle"
              />
              By default, allow estimators to include uploaded supplier PDFs in the combined export
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark disabled:opacity-60"
              data-testid="save-pdf-template-button"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Template"}
            </button>
            {savedAt && <span className="text-xs text-slate-500">Saved · {savedAt.toLocaleTimeString()}</span>}
          </div>
        </div>

        {/* RIGHT: live preview of the header & footer */}
        <div className="qb-card p-4 flex flex-col" style={{ minHeight: "60vh" }}>
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h3 className="font-display text-base font-bold uppercase tracking-wider flex items-center gap-2">
              <Eye size={16} /> Preview
            </h3>
            <select
              value={previewQuoteId}
              onChange={(e) => setPreviewQuoteId(e.target.value)}
              className="h-8 border border-slate-300 px-2 text-xs bg-white max-w-[260px]"
              data-testid="pdf-preview-quote-select"
            >
              {previewQuotes.length === 0 && <option value="">No quotes available</option>}
              {previewQuotes.map((p) => (
                <option key={p.id} value={p.id}>{p.quote_number} · {p.customer_name}</option>
              ))}
            </select>
          </div>

          <div className="text-[11px] text-slate-400 mb-2">
            Header band &amp; footer text only — the rest of the planner layout (tiles, scope, labour, materials, hire) is generated from the selected quote and exported as-is.
          </div>

          {/* Preview: mimic the planner header band styling */}
          <div className="rounded overflow-hidden border border-slate-200">
            <div className="text-white px-5 py-4 flex items-start justify-between gap-4" style={{ background: "#0E1A33" }}>
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em]" style={{ color: "#E66A2C" }}>
                  {tpl.title || "Planner Sheet · For internal scheduling"}
                </div>
                <div className="font-mono text-2xl font-black tracking-tight mt-1">
                  {previewQuote?.quote_number || "QUO-PREVIEW"}
                </div>
                <div className="text-[11px] mt-1 opacity-90">
                  {(previewQuote?.customer_name || "Sample Customer Ltd")} · {(previewQuote?.category || previewQuote?.job_type || "Install")}
                </div>
                {tpl.header_text && (
                  <div className="text-[10px] mt-1.5 opacity-80 whitespace-pre-wrap max-w-[380px]">{subst(tpl.header_text)}</div>
                )}
              </div>
              <div className="text-right text-[11px] leading-relaxed">
                <div className="opacity-70 uppercase tracking-wider text-[9px]">Branch</div>
                <div>{previewQuote?.branch || "—"}</div>
              </div>
            </div>

            {/* Body placeholder so we visually demonstrate "header + content + footer" */}
            <div className="bg-slate-50 text-center text-[10px] uppercase tracking-wider text-slate-400 py-10">
              · Planner content from the selected quote ·
            </div>

            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 text-center px-4 py-3 border-t border-slate-100 whitespace-pre-wrap">
              {tpl.footer_text
                ? subst(tpl.footer_text)
                : `Entry Group · Quote Matrix · ${previewQuote?.quote_number || "QUO-PREVIEW"} · Generated ${new Date().toLocaleDateString("en-GB")}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KeywordChips({ onPick, keywords }) {
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {keywords.map((kw) => (
        <button
          key={kw}
          type="button"
          onClick={() => onPick(kw)}
          className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 hover:bg-brand-blue hover:text-white text-slate-700"
          data-testid={`pdf-keyword-${kw.replace(/[{}]/g, "")}`}
        >
          {kw}
        </button>
      ))}
    </div>
  );
}
